/// <reference types="chrome" />
/**
 * Web/Frontend API for scribe-messaging
 *
 * This module provides generic messaging functions for web applications
 * to communicate with Chrome extensions.
 */

import * as React from 'react';

/**
 * Get the extension ID based on environment
 */
function getExtensionId(): string | undefined {
  // This should be configured by the app using environment variables
  // or passed as a parameter
  if (typeof window !== 'undefined' && (window as any).SCRIBE_EXTENSION_ID) {
    return (window as any).SCRIBE_EXTENSION_ID;
  }

  // Default extension IDs - should be overridden in production
  const extensionIds = {
    development: 'your-dev-extension-id',
    staging: 'your-staging-extension-id',
    production: 'your-prod-extension-id',
  };

  const env = process.env.NODE_ENV || 'development';
  return extensionIds[env as keyof typeof extensionIds];
}

/**
 * Response format for extension messages
 */
export type ExtensionMessageResponse<T> = {
  data: T | undefined;
  error: string | undefined;
};

/**
 * Send a message to a Chrome extension from a web page
 *
 * @param type - The message type
 * @param payload - Optional message payload
 * @param extensionId - Optional extension ID (uses configured ID if not provided)
 * @returns Promise resolving to the response with data/error format
 */
export function sendMessageToExtension<T = any>(
  type: string,
  payload?: any,
  extensionId?: string,
): Promise<ExtensionMessageResponse<T>> {
  return new Promise((resolve) => {
    // Use provided extension ID or get from environment
    const finalExtensionId = extensionId || getExtensionId();

    if (!finalExtensionId) {
      resolve({
        data: undefined,
        error:
          'Extension ID not configured. Please set SCRIBE_EXTENSION_ID or pass extensionId parameter.',
      });
      return;
    }

    const message: any = { type };
    if (payload !== undefined) {
      message.payload = payload;
    }

    // Check if chrome.runtime is available
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      resolve({
        data: undefined,
        error:
          'Chrome runtime API is not available. This function must be called from a Chrome extension context or a page with proper extension permissions.',
      });
      return;
    }

    try {
      chrome.runtime.sendMessage(finalExtensionId, message, (response: any) => {
        if (chrome.runtime.lastError) {
          resolve({
            data: undefined,
            error: chrome.runtime.lastError.message,
          });
          return;
        }

        if (response && response.error) {
          resolve({
            data: undefined,
            error: response.error,
          });
          return;
        }

        resolve({
          data: response as T,
          error: undefined,
        });
      });
    } catch (error) {
      resolve({
        data: undefined,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  });
}

/**
 * React hook for listening to messages from the extension via window.postMessage
 */
export function useExtensionMessage<T = any>(
  handler: (message: T) => void,
  deps: React.DependencyList = [],
) {
  React.useEffect(() => {
    const listener = (event: MessageEvent) => {
      // Only accept messages from the extension
      if (event.source !== window) return;

      // You might want to add additional origin checks here
      if (event.data && event.data.source === 'scribe-extension') {
        handler(event.data);
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, deps);
}

/**
 * Check if we're in a context where we can communicate with extensions
 */
export function canCommunicateWithExtension(): boolean {
  return (
    typeof chrome !== 'undefined' &&
    chrome.runtime &&
    typeof chrome.runtime.sendMessage === 'function'
  );
}

/**
 * Configure the extension ID globally
 */
export function configureExtension(config: {
  extensionId?: string;
  extensionIds?: {
    development?: string;
    staging?: string;
    production?: string;
  };
}) {
  if (config.extensionId) {
    (window as any).SCRIBE_EXTENSION_ID = config.extensionId;
  } else if (config.extensionIds) {
    const env = process.env.NODE_ENV || 'development';
    const id = config.extensionIds[env as keyof typeof config.extensionIds];
    if (id) {
      (window as any).SCRIBE_EXTENSION_ID = id;
    }
  }
}
